import "./../topicStartegyBuilder/GetKeywordsTable.scss";

import {
  Box,
  Card,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

const Suggestions = ({ apiResponse }: any) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(["All"]);
  const [sortConfig, setSortConfig] = useState({
    key: "search volume",
    direction: "desc",
  });

  const columnKeyMap: Record<string, string> = {
    "search volume": "search_volume",
    "relevance score": "business_relevance_score",
    "relevance level": "business_relevance_level",
  };

  const pageTypes = useMemo(() => {
    const types = [...new Set(apiResponse.map((item: any) => item.page_type))];
    return ["All", ...types];
  }, [apiResponse]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
    setPage(0);
  };

  const handleFilterClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (option: string) => {
    if (option === "All") {
      setSelectedFilters(["All"]);
    } else {
      let updated = selectedFilters.includes(option)
        ? selectedFilters.filter((item) => item !== option)
        : [...selectedFilters.filter((item) => item !== "All"), option];

      if (updated.length === 0) {
        updated = ["All"];
      }

      setSelectedFilters(updated);
    }
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const filteredData = apiResponse
    .filter((row: any) => {
      const matchesSearch =
        searchTerm === "" ||
        row.keyword.toLowerCase().includes(searchTerm) ||
        row.page_type.toLowerCase().includes(searchTerm);

      const matchesFilter =
        selectedFilters.includes("All") ||
        selectedFilters.includes(row.page_type);

      return matchesSearch && matchesFilter;
    })
    .sort((a: any, b: any) => {
      const dataKey = columnKeyMap[sortConfig.key];
      if (!dataKey) return 0;

      const aVal = a[dataKey];
      const bVal = b[dataKey];

      if (aVal == null || bVal == null) return 0;

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    });

  const renderRelevanceLabel = (level: string) => {
    const label = level?.toLowerCase();
    const colors: any = {
      high: "success",
      medium: "warning",
      low: "error",
    };
    return (
      <Chip
        label={level}
        color={colors[label] || "default"}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Card className="secondtab-card-container">
      <Box className="keywords-card-header">
        <Typography variant="h5">Suggestions</Typography>
        <Typography
          variant="body2"
          sx={{
            color: "var(--text-secondary, rgba(0, 0, 0, 0.60))",
            opacity: 0.6,
            fontFeatureSettings: "'liga' off, 'clig' off",
          }}
        >
          {apiResponse.length} total suggestions
        </Typography>
      </Box>

      <Box className="search-container">
        <Box className="search-filter">
          <TextField
            fullWidth
            label="Search Keywords"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search keywords..."
          />
          <IconButton onClick={handleFilterClick}>
            <FilterAltIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleFilterClose}
            MenuListProps={{ onMouseLeave: handleFilterClose }}
          >
            {pageTypes.map((type: any) => (
              <MenuItem key={type} onClick={() => handleFilterChange(type)}>
                <Checkbox checked={selectedFilters.includes(type)} />
                <ListItemText primary={type} />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "auto", mt: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  "classification",
                  "keyword",
                  "page type",
                  "parent kw",
                  "search intent",
                  "search volume",
                  "review comparison",
                  "relevance score",
                  "relevance level",
                ].map((key) => (
                  <TableCell
                    key={key}
                    onClick={() => handleSort(key)}
                    style={{
                      cursor: key === "keyword" ? "default" : "pointer",
                    }}
                  >
                    {key.toUpperCase()}{" "}
                    {sortConfig.key === key &&
                      (sortConfig.direction === "asc" ? (
                        <ArrowUpwardIcon fontSize="small" sx={{ ml: 2 }} />
                      ) : (
                        <ArrowDownwardIcon fontSize="small" sx={{ ml: 2 }} />
                      ))}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>{row.classification}</TableCell>
                    <TableCell>{row.keyword}</TableCell>
                    <TableCell>{row.page_type}</TableCell>
                    <TableCell>{row.parent_kw}</TableCell>
                    <TableCell>{row.search_intent}</TableCell>
                    <TableCell>{row.search_volume || "0"}</TableCell>
                    <TableCell>{row.review_comparison}</TableCell>
                    <TableCell>{row.business_relevance_score}</TableCell>
                    <TableCell>
                      {renderRelevanceLabel(row.business_relevance_level)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[100, 200, 500, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Card>
  );
};

export default Suggestions;
